import { useEffect, useState } from 'react'
import { api } from '../api.ts'
import { money } from '../format.ts'

// ── Shapes (mirror raporty.ts) ────────────────────────────────────────────────
interface SalesRow {
  etykieta: string
  sztuk: number
  sztuk_bez_kosztu: number
  przychod_net: string | null
  przychod_znany_koszt: string | null
  marza_net: string | null
  marza_proc: string | null
}
interface Report {
  okres: { od: string; do: string }
  sprzedaz: {
    podsumowanie: SalesRow | null
    wgProducenta: SalesRow[]
    wgHandlowca: SalesRow[]
    wgKategorii: SalesRow[]
    uzgodnienie: { wydanych_w_okresie: number; wydanych_bez_pozycji: number } | null
  }
  lejek: {
    podsumowanie: {
      wystawione: number
      zaakceptowane: number
      odrzucone: number
      wygasle: number
      otwarte: number
      konwersja_pct: string | null
      konwersja_rozstrzygniete_pct: string | null
      wartosc_wygrana_pln: string | null
    } | null
    czasDecyzji: { z_decyzja: number; bez_znacznika: number; sredni_czas_dni: string | null } | null
  }
  magazyn: {
    stany: { stan: string; liczba: number }[]
    zalegania: { sztuk_zalegajacych: number; sztuk_bez_kosztu: number; zamrozony_kapital_net: string | null } | null
    zalegajace: { unit_id: string; serial_number: string | null; producent: string; model: string; dni_na_placu: number; koszt_net: string | null }[]
  }
  serwis: {
    podsumowanie: { zgloszenia: number; zakonczone: number; sredni_czas_realizacji_dni: string | null } | null
    kosztyWgProducenta: {
      producent: string
      zgloszen: number
      koszt_gwarancyjny_net: string | null
      uznane_net: string | null
      zlozone_net: string | null
      odrzucone_net: string | null
      bez_roszczenia_net: string | null
    }[]
  }
}

const STAN_LABEL: Record<string, string> = {
  wydane: 'Wydane',
  sprzedane_do_wydania: 'Sprzedane, do wydania',
  zarezerwowane: 'Zarezerwowane',
  dostepne: 'Dostępne',
  w_drodze: 'W drodze',
}

/** Bar sized against the biggest value in its group. Dense, no chart library (SPEC 5.7). */
function Bar({ value, max, tone = 'brand' }: { value: number; max: number; tone?: 'brand' | 'red' | 'green' | undefined }) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0
  const bg = tone === 'red' ? 'bg-red-400' : tone === 'green' ? 'bg-green-500' : 'bg-brand-500'
  return (
    <div className="h-1.5 bg-slate-100 rounded overflow-hidden">
      <div className={`h-full ${bg}`} style={{ width: `${pct}%` }} />
    </div>
  )
}

export function ReportsPage() {
  const [r, setR] = useState<Report | null>(null)
  const [error, setError] = useState<string | null>(null)
  const today = new Date().toISOString().slice(0, 10)
  const [od, setOd] = useState(`${today.slice(0, 4)}-01-01`)
  const [doD, setDoD] = useState(today)

  function load() {
    api
      .get<Report>(`/api/raporty?od=${od}&do=${doD}`)
      .then(setR)
      .catch((e) => setError(e instanceof Error ? e.message : 'Błąd'))
  }
  useEffect(load, [od, doD])

  if (error) return <div className="p-6 text-red-700">{error}</div>
  if (!r) return <div className="p-6 text-slate-400">Ładowanie…</div>

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center justify-between gap-4 mb-5">
        <h1 className="text-xl font-bold text-slate-800">Raporty</h1>
        <div className="flex items-center gap-2 text-sm">
          <label className="text-slate-500">od</label>
          <input type="date" value={od} onChange={(e) => setOd(e.target.value)} className="px-2 py-1 border border-slate-300 rounded text-sm" />
          <label className="text-slate-500">do</label>
          <input type="date" value={doD} onChange={(e) => setDoD(e.target.value)} className="px-2 py-1 border border-slate-300 rounded text-sm" />
        </div>
      </div>

      {/* SPRZEDAŻ I MARŻA */}
      <Section title="Sprzedaż i marża">
        {r.sprzedaz.podsumowanie ? (
          <>
            <div className="flex flex-wrap gap-4 mb-4">
              <Kpi label="Sprzedanych maszyn" value={String(r.sprzedaz.podsumowanie.sztuk)} />
              <Kpi label="Przychód netto" value={money(r.sprzedaz.podsumowanie.przychod_net)} />
              <Kpi label="Marża" value={money(r.sprzedaz.podsumowanie.marza_net)} sub={r.sprzedaz.podsumowanie.marza_proc ? `${r.sprzedaz.podsumowanie.marza_proc}%` : undefined} tone="green" />
            </div>
            {r.sprzedaz.uzgodnienie && r.sprzedaz.uzgodnienie.wydanych_bez_pozycji > 0 && (
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-1.5 mb-3">
                ⚠ {r.sprzedaz.uzgodnienie.wydanych_bez_pozycji} z {r.sprzedaz.uzgodnienie.wydanych_w_okresie} wydanych maszyn nie ma powiązanej pozycji zamówienia — poza przychodem powyżej.
              </div>
            )}
            <SalesTable title="Wg producenta" rows={r.sprzedaz.wgProducenta} />
            <SalesTable title="Wg handlowca" rows={r.sprzedaz.wgHandlowca} />
            <SalesTable title="Wg kategorii" rows={r.sprzedaz.wgKategorii} />
            <p className="text-[11px] text-slate-400 mt-1">Marża % liczona nad maszynami ze znanym kosztem zakupu. Maszyny bez wpisanego kosztu są w przychodzie, ale poza marżą.</p>
          </>
        ) : (
          <Empty>Brak sprzedanych maszyn w tym okresie.</Empty>
        )}
      </Section>

      {/* LEJEK OFERT */}
      <Section title="Lejek ofert i konwersja">
        {r.lejek.podsumowanie && r.lejek.podsumowanie.wystawione > 0 ? (
          <>
            <div className="flex flex-wrap gap-4 mb-3">
              <Kpi label="Wystawione" value={String(r.lejek.podsumowanie.wystawione)} />
              <Kpi label="Zaakceptowane" value={String(r.lejek.podsumowanie.zaakceptowane)} tone="green" />
              <Kpi label="Konwersja" value={r.lejek.podsumowanie.konwersja_pct ? `${r.lejek.podsumowanie.konwersja_pct}%` : '—'} sub={r.lejek.podsumowanie.konwersja_rozstrzygniete_pct ? `${r.lejek.podsumowanie.konwersja_rozstrzygniete_pct}% z rozstrzygniętych` : undefined} />
              <Kpi label="Wartość wygrana" value={money(r.lejek.podsumowanie.wartosc_wygrana_pln)} />
              {r.lejek.czasDecyzji?.sredni_czas_dni != null && <Kpi label="Śr. czas do decyzji" value={`${r.lejek.czasDecyzji.sredni_czas_dni} dni`} />}
            </div>
            <div className="space-y-1.5 max-w-md">
              <FunnelRow label="Wystawione" value={r.lejek.podsumowanie.wystawione} max={r.lejek.podsumowanie.wystawione} />
              <FunnelRow label="Otwarte (gorące)" value={r.lejek.podsumowanie.otwarte} max={r.lejek.podsumowanie.wystawione} />
              <FunnelRow label="Zaakceptowane" value={r.lejek.podsumowanie.zaakceptowane} max={r.lejek.podsumowanie.wystawione} tone="green" />
              <FunnelRow label="Odrzucone" value={r.lejek.podsumowanie.odrzucone} max={r.lejek.podsumowanie.wystawione} tone="red" />
              <FunnelRow label="Wygasłe (martwe)" value={r.lejek.podsumowanie.wygasle} max={r.lejek.podsumowanie.wystawione} tone="red" />
            </div>
            {r.lejek.czasDecyzji && r.lejek.czasDecyzji.bez_znacznika > 0 && (
              <p className="text-[11px] text-slate-400 mt-2">{r.lejek.czasDecyzji.bez_znacznika} ofert bez znacznika czasu decyzji — poza średnią.</p>
            )}
          </>
        ) : (
          <Empty>Brak wystawionych ofert w tym okresie.</Empty>
        )}
      </Section>

      {/* MAGAZYN */}
      <Section title="Magazyn">
        <div className="flex flex-wrap gap-3 mb-3">
          {r.magazyn.stany.filter((s) => s.stan !== 'wydane').map((s) => (
            <Kpi key={s.stan} label={STAN_LABEL[s.stan] ?? s.stan} value={String(s.liczba)} />
          ))}
        </div>
        {r.magazyn.zalegania && r.magazyn.zalegania.sztuk_zalegajacych > 0 ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="text-sm text-red-800 font-medium mb-2">
              Zalegają na placu ({r.magazyn.zalegania.sztuk_zalegajacych}) — zamrożony kapitał {money(r.magazyn.zalegania.zamrozony_kapital_net)}
              {r.magazyn.zalegania.sztuk_bez_kosztu > 0 && <span className="text-xs text-red-600"> ({r.magazyn.zalegania.sztuk_bez_kosztu} bez wpisanego kosztu)</span>}
            </div>
            <table className="w-full text-sm">
              <tbody>
                {r.magazyn.zalegajace.map((z) => (
                  <tr key={z.unit_id} className="border-t border-red-100">
                    <td className="py-1 font-mono text-slate-700">{z.serial_number ?? '—'}</td>
                    <td className="py-1 text-slate-600">{z.model} · {z.producent}</td>
                    <td className="py-1 text-right text-red-700 font-medium">{z.dni_na_placu} dni</td>
                    <td className="py-1 text-right tabular text-slate-600">{money(z.koszt_net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-slate-400">Nic nie zalega powyżej 180 dni.</p>
        )}
      </Section>

      {/* SERWIS */}
      <Section title="Serwis">
        <div className="flex flex-wrap gap-4 mb-3">
          <Kpi label="Zgłoszenia" value={String(r.serwis.podsumowanie?.zgloszenia ?? 0)} />
          <Kpi label="Zakończone" value={String(r.serwis.podsumowanie?.zakonczone ?? 0)} />
          {r.serwis.podsumowanie?.sredni_czas_realizacji_dni != null && <Kpi label="Śr. czas realizacji" value={`${r.serwis.podsumowanie.sredni_czas_realizacji_dni} dni`} />}
        </div>
        {r.serwis.kosztyWgProducenta.length > 0 ? (
          <div>
            <div className="text-xs uppercase text-slate-400 mb-1">Koszty gwarancyjne wg producenta (do negocjacji)</div>
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-400">
                <tr>
                  <th className="text-left font-medium py-1">Producent</th>
                  <th className="text-right font-medium py-1">Zgł.</th>
                  <th className="text-right font-medium py-1">Nasz koszt</th>
                  <th className="text-right font-medium py-1">Uznane</th>
                  <th className="text-right font-medium py-1">W toku</th>
                  <th className="text-right font-medium py-1">Odrzucone</th>
                </tr>
              </thead>
              <tbody>
                {r.serwis.kosztyWgProducenta.map((k) => (
                  <tr key={k.producent} className="border-t border-slate-100">
                    <td className="py-1 text-slate-700">{k.producent}</td>
                    <td className="py-1 text-right text-slate-500">{k.zgloszen}</td>
                    <td className="py-1 text-right tabular font-medium text-slate-800">{money(k.koszt_gwarancyjny_net)}</td>
                    <td className="py-1 text-right tabular text-green-700">{money(k.uznane_net)}</td>
                    <td className="py-1 text-right tabular text-amber-700">{money(k.zlozone_net)}</td>
                    <td className="py-1 text-right tabular text-red-700">{money(k.odrzucone_net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[11px] text-slate-400 mt-1">„Odrzucone" to nasz koszt, którego producent nie zwrócił — argument do rozmowy.</p>
          </div>
        ) : (
          <Empty>Brak zgłoszeń serwisowych w tym okresie.</Empty>
        )}
      </Section>
    </div>
  )
}

// ── Small building blocks ─────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
      <h2 className="font-semibold text-slate-700 text-sm mb-3">{title}</h2>
      {children}
    </section>
  )
}
function Kpi({ label, value, sub, tone }: { label: string; value: string; sub?: string | undefined; tone?: 'green' | undefined }) {
  return (
    <div>
      <div className="text-xs text-slate-400">{label}</div>
      <div className={`text-lg font-bold tabular ${tone === 'green' ? 'text-green-700' : 'text-slate-800'}`}>{value}</div>
      {sub && <div className="text-xs text-slate-400">{sub}</div>}
    </div>
  )
}
function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-slate-400">{children}</p>
}
function FunnelRow({ label, value, max, tone }: { label: string; value: number; max: number; tone?: 'red' | 'green' | undefined }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-40 text-slate-600 shrink-0">{label}</span>
      <div className="flex-1">
        <Bar value={value} max={max} tone={tone} />
      </div>
      <span className="w-8 text-right tabular text-slate-700">{value}</span>
    </div>
  )
}
function SalesTable({ title, rows }: { title: string; rows: SalesRow[] }) {
  if (rows.length === 0) return null
  const max = Math.max(...rows.map((r) => Number(r.marza_net ?? 0)), 1)
  return (
    <div className="mb-3">
      <div className="text-xs uppercase text-slate-400 mb-1">{title}</div>
      <table className="w-full text-sm">
        <tbody>
          {rows.map((r) => (
            <tr key={r.etykieta} className="border-t border-slate-100">
              <td className="py-1 text-slate-700">{r.etykieta}</td>
              <td className="py-1 text-right text-slate-500 w-12">{r.sztuk} szt</td>
              <td className="py-1 text-right tabular text-slate-600 w-32">{money(r.przychod_net)}</td>
              <td className="py-1 w-32">
                <Bar value={Number(r.marza_net ?? 0)} max={max} tone="green" />
              </td>
              <td className="py-1 text-right tabular font-medium text-green-700 w-28">
                {money(r.marza_net)}
                {r.marza_proc && <span className="text-xs text-slate-400 ml-1">{r.marza_proc}%</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
